import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Swaps variant metaobject list markup (tabs or grid) on variant:update. Tab selection uses event delegation.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} surface
 *
 * @extends {Component<Refs>}
 */
class PromoVariantMetaobjectList extends Component {
  requiredRefs = ['surface'];

  /** @type {Record<string, { html: string }> | null} */
  #map = null;

  /** @type {AbortController} */
  #uiAbort = new AbortController();

  /** @type {AbortController | null} */
  #variantAbort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#uiAbort = new AbortController();
    this.#parseMap();
    this.#bindVariantListener();
    this.addEventListener('click', this.#onBlockClick, { signal: this.#uiAbort.signal });
    this.addEventListener('keydown', this.#onBlockKeydown, { signal: this.#uiAbort.signal });
  }

  disconnectedCallback() {
    this.#uiAbort.abort();
    this.#variantAbort?.abort();
    super.disconnectedCallback();
  }

  #parseMap() {
    const el = this.querySelector('script[type="application/json"][data-promo-map]');
    if (!el?.textContent) return;
    try {
      this.#map = JSON.parse(el.textContent.trim());
    } catch {
      this.#map = null;
    }
  }

  #bindVariantListener() {
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    this.#variantAbort = new AbortController();
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal: this.#variantAbort.signal });
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant?.id || !this.#map || !this.refs.surface) return;

    const entry = this.#getMapEntryForVariantId(variant.id);
    if (entry) {
      this.refs.surface.innerHTML = entry.html ?? '';
    }
  };

  /**
   * @param {string|number} rawId
   * @returns {{ html: string } | undefined}
   */
  #getMapEntryForVariantId(rawId) {
    if (!this.#map) return undefined;
    const idStr = String(rawId);
    const keys = [idStr, rawId];
    const gidMatch = typeof rawId === 'string' && rawId.includes('gid://') ? rawId.match(/\d+$/) : null;
    if (gidMatch) keys.push(gidMatch[0]);

    for (const key of keys) {
      if (key === undefined || key === null) continue;
      const val = this.#map[key];
      if (val && typeof val === 'object' && 'html' in val) {
        return val;
      }
    }
    return undefined;
  }

  /**
   * @param {Event} e
   */
  #onBlockClick = (e) => {
    if (this.dataset.layout !== 'tabs' || !this.refs.surface) return;
    const t = e.target;
    if (!(t instanceof HTMLButtonElement) || !t.hasAttribute('data-pmo-tab')) return;
    e.preventDefault();
    this.#activateTab(t, this.refs.surface);
  };

  /**
   * @param {Event} e
   */
  #onBlockKeydown = (e) => {
    if (this.dataset.layout !== 'tabs' || !this.refs.surface) return;
    if (!(e instanceof KeyboardEvent)) return;
    const t = e.target;
    if (!(t instanceof HTMLButtonElement) || !t.hasAttribute('data-pmo-tab')) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;

    const tablist = t.closest('.promo-vml__tablist[role="tablist"]');
    if (!tablist) return;
    const tabs = Array.from(tablist.querySelectorAll('[data-pmo-tab]'));
    const i = tabs.indexOf(t);
    if (i < 0) return;
    e.preventDefault();
    const last = tabs.length - 1;
    let next = i;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    else if (e.key === 'ArrowLeft') next = i <= 0 ? last : i - 1;
    else if (e.key === 'ArrowRight') next = i >= last ? 0 : i + 1;
    const nextBtn = tabs[next];
    if (nextBtn instanceof HTMLButtonElement) {
      this.#activateTab(nextBtn, this.refs.surface);
      nextBtn.focus();
    }
  };

  /**
   * @param {HTMLButtonElement} tab
   * @param {HTMLElement} root
   */
  #activateTab(tab, root) {
    const tablist = tab.closest('.promo-vml__tablist[role="tablist"]');
    if (!tablist) return;
    const tabs = tablist.querySelectorAll('[data-pmo-tab]');
    tabs.forEach((b) => {
      if (b instanceof HTMLButtonElement) {
        const isSel = b === tab;
        b.setAttribute('aria-selected', isSel ? 'true' : 'false');
        b.setAttribute('tabindex', isSel ? '0' : '-1');
        const c = b.getAttribute('aria-controls');
        if (c) {
          const p = root.querySelector(`#${CSS.escape(c)}`);
          if (p instanceof HTMLElement) {
            p.hidden = !isSel;
          }
        }
      }
    });
  }
}

if (!customElements.get('promo-variant-metaobject-list')) {
  customElements.define('promo-variant-metaobject-list', PromoVariantMetaobjectList);
}
